import { Box, Stack, Typography } from "@mui/material";
import React, { ReactElement, useMemo } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  ReferenceDot,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from "recharts";

import { useTheme } from "@emotion/react";
import styled from "styled-components";
import { useApp } from "../../../contexts/AppContext";
import { useReport } from "../../../contexts/ReportContext";

const StyledLegend = styled(Stack)(() => ({
  backgroundColor: "transparent",
  borderRadius: "0",
  border: "none",
  padding: "10px",
  margin: "0 auto",
  justifyContent: "space-around",
  paddingLeft: 40,
  marginTop: 20,
}));

const StyledContainer = styled(Box)(() => ({
  position: "relative",
  "& .recharts-tooltip-wrapper": {
    zIndex: 1000,
  },
}));

export interface AverageEggsChartData {
  age: number;
  error: [number, number];
  numEggs: number;
  numEggsLow: number;
  numEggsHigh: number;
}

interface AverageEggsChartProps {
  width: number;
  height: number;
  data: AverageEggsChartData[];
  age: number;
  eggs: number;
}

const CustomTooltip: React.FC<TooltipProps<number, string>> = ({
  active,
  payload,
  label,
}) => {
  const newPayload = payload?.filter((x: any) => x.dataKey !== "error") || [];
  if (!active || !newPayload.length) return null;
  return (
    <StyledLegend sx={{ backgroundColor: "white" }} gap={1}>
      <Typography variant="report.card.info.normal" color="black">
        Average number of eggs: {newPayload[0].payload.numEggs}
      </Typography>
      <Typography variant="report.card.info.normal" color="black">
        Error: {newPayload[0].payload.error[0]} -{" "}
        {newPayload[0].payload.error[1]}
      </Typography>
    </StyledLegend>
  );
};

const AverageEggsChart: React.FC<AverageEggsChartProps> = ({
  width,
  height,
  data,
  age,
  eggs,
}) => {
  const { colorStyle } = useReport();
  const theme: any = useTheme();

  const { translate } = useApp();

  const renderLegend = useMemo(() => {
    const labelMap: { [k: string]: ReactElement } = {
      [translate(
        "eggfreeze",
        "report.estimatedResults.average.legends.prediction",
        {
          from: `${Math.floor(eggs * 0.8)}`,
          to: `${Math.ceil(eggs * 1.2)}`,
        }
      )]: (
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%", // Makes it a circle
            backgroundColor: "gray", // Gray fill
            border: "2px solid black", // Black boundary
          }}
        />
      ),
      [translate(
        "eggfreeze",
        "report.estimatedResults.average.legends.population"
      )]: (
        <Box
          width={25}
          height={25}
          sx={{
            backgroundColor: theme.palette.reportColorStyles[colorStyle].light,
          }}
        />
      ),
    };
    return (
      <StyledLegend direction="row" gap={1}>
        {Object.keys(labelMap).map((label, index) => (
          <Stack key={index} gap={1} direction={"row"} alignItems={"center"}>
            {labelMap[label]}
            <Typography
              textAlign={"left"}
              variant="report.card.info.normal"
              color="black"
            >
              {label}
            </Typography>
          </Stack>
        ))}
      </StyledLegend>
    );
  }, [colorStyle, eggs]);

  return (
    <StyledContainer sx={{ position: "relative", width, height }}>
      <ComposedChart
        width={width}
        height={height}
        data={data}
        margin={{
          top: 10,
          right: 30,
          left: 0,
          bottom: 0,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <YAxis
          dataKey="numEggs"
          label={{
            value: translate(
              "eggfreeze",
              "report.estimatedResults.average.yAxis"
            ),
            angle: -90,
            position: "insideBottomLeft",
            offset: 14,
          }}
        />
        <XAxis
          dataKey="age" // Use the correct key for age
          label={{
            value: translate(
              "eggfreeze",
              "report.estimatedResults.average.xAxis"
            ),
            position: "bottom",
            offset: -10,
          }}
          ticks={[20, 25, 30, 35, 40, 45]} // Example tick values
          tick={{ fontSize: 12, fill: "#000" }} // Customize tick appearance
        />
        {/* <XAxis
          dataKey="name"
          label={{ value: "Age (years)", position: "bottom", offset: -10 }}
        /> */}
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="error"
          stroke="none"
          fill={theme.palette.reportColorStyles[colorStyle].light}
          connectNulls
          dot={false}
          activeDot={false}
        />

        <ReferenceDot
          x={age} // Center the dot at the age
          y={eggs} // Center the dot at the egg count
          r={4} // Radius of the dot
          fill="gray" // Fill color
          stroke="black" // Outline color
          strokeWidth={1} // Outline thickness
        />

        <Legend
          content={renderLegend}
          wrapperStyle={{
            height: "auto",
            position: "absolute",
            bottom: 0,
            border: "none",
          }}
        />
      </ComposedChart>
    </StyledContainer>
  );
};

export default AverageEggsChart;
